import { DatePipe, DecimalPipe, isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  inject,
} from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { GROWTHBOOK } from '../../growthbook';
import { parseServerTiming } from '../../lib/parse-server-timing';
import { NetworkInterceptorService } from '../../network-interceptor';
import { InputFieldComponent } from '../../shared-extra/components/input-field/input-field.component';
import { TogglerComponent } from '../../shared-extra/components/toggler/toggler.component';
import { SvgComponent } from '../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../shared/directives/button.directive';
import { TooltipDirective } from '../../shared/directives/tooltip.directive';
import { SortByPipe } from '../../shared/pipes/sort-by.pipe';

@Component({
  selector: 'app-server-timing',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './server-timing.component.html',
  styleUrls: ['./server-timing.component.scss'],
  imports: [
    DatePipe,
    DecimalPipe,
    TogglerComponent,
    TooltipDirective,
    SortByPipe,
    SvgComponent,
    ButtonDirective,
    FormsModule,
    ReactiveFormsModule,
    InputFieldComponent,
  ],
})
export class ServerTimingComponent implements OnInit, OnDestroy {
  private check = inject(ChangeDetectorRef);
  private networkInterceptor = inject(NetworkInterceptorService);
  private platformId = inject<string>(PLATFORM_ID);
  private growthBook = inject(GROWTHBOOK);

  serverTimingData: {
    rows: { type: string; id: string; description: string; timeTaken: number }[];
    totalTimeTaken: number;
    url: string;
    method: string;
    requestBody: string;
    responseTime: number;
  }[] = [];

  destroyed$ = new Subject<void>();

  openStates: { [key: string]: boolean } = {};
  search = '';
  form = new FormGroup({
    search: new FormControl(''),
  });

  ngOnInit(): void {
    this.form.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(value => {
      this.search = value.search;
      this.check.detectChanges();
    });

    this.networkInterceptor.response$.pipe(takeUntil(this.destroyed$)).subscribe(({ res, req }) => {
      if (
        this.growthBook.isOn('server-timing') &&
        isPlatformBrowser(this.platformId) &&
        res?.headers?.has('Server-Timing')
      ) {
        this.serverTimingData.push({
          ...parseServerTiming(res.headers.get('Server-Timing') || ''),
          url: req.url,
          requestBody: req.body ? JSON.stringify(req.body, null, 2) : '',
          method: req.method,
          responseTime: Date.now(),
        });
        if (this.serverTimingData.length > 100) {
          this.serverTimingData.shift();
        }
      }
    });
  }

  filterBySearch = (items: typeof this.serverTimingData) => {
    if (this.search.startsWith('method:')) {
      const method = this.search.split(':')[1].trim().toUpperCase();
      return items.filter(item => item.method.startsWith(method));
    }

    return items.filter(item => {
      return (
        item.url.toLowerCase().includes(this.search) ||
        item.rows.some(row => row.description.toLowerCase().includes(this.search))
      );
    });
  };

  addMark() {
    const latest = this.serverTimingData[this.serverTimingData.length - 1];
    this.serverTimingData.push(<(typeof this.serverTimingData)[number]>{
      responseTime: latest.responseTime + 1,
      url: 'mark',
    });
    this.check.detectChanges();
  }

  cleanUrl(url: string) {
    return url
      .replace(/.*\/v1\//, '/')
      .replace(/&r=.*/, '')
      .replace(/\?r=.*/, '');
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
